/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {createContext, ReactNode} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {PrimaryButton} from '../../components/buttons/current/PrimaryButton';
import {TertiaryButton} from '../../components/buttons/current/TertiaryButton';
import {GeofencesDrawIcon} from '../../icons/current/GeofencesDrawIcon';
import {GeofencesPolygonIcon} from '../../icons/current/GeofencesPolygonIcon';
import {GeofencesRadialIcon} from '../../icons/current/GeofencesRadialIcon';
import {dlsColor} from '../../theme/theme';
import {CreateGeoFenceTypeEnum, IconProps, IconSize} from '../../types/types';
import {CreateGeofenceCardHeader} from './CreateGeofenceCardHeader';
import {useGeofenceCardStyles} from './geofenceCardStyles';
import {GeofenceRadius, GeofenceRadiusControl} from './GeofenceRadiusControl';

// defines what the current theme's create geofence card needs from
// CreateGeofences, read by the location and save steps rendered below it ...
export type CreateGeofenceCardContextValue = {
  selectedGeofenceType: CreateGeoFenceTypeEnum;
  onChangeGeofenceType: (geofenceType: CreateGeoFenceTypeEnum) => void;
  onClose: () => void;
  // set by the save step while it's shown, which sizes the card to fit it ...
  setIsSaveStep: (isSaveStep: boolean) => void;
};

export type CreateGeofenceCardProps = {
  selectedGeofenceType: CreateGeoFenceTypeEnum;
  onChangeGeofenceType: (geofenceType: CreateGeoFenceTypeEnum) => void;
  onClose: () => void;
  onCreate: () => void;
  isCreateDisabled: boolean;
  search: ReactNode;
  radius?: GeofenceRadius | null;
};

type GeofenceTypeOption = {
  type: CreateGeoFenceTypeEnum;
  key: 'radial' | 'polygon' | 'draw';
  Icon: (props: IconProps) => JSX.Element;
};

/**
 * Provides the current theme's create geofence card state to the location
 * and save steps rendered inside CreateGeofences - `null` everywhere else
 * (the legacy theme, boundary creation and editing), where those steps keep
 * their own legacy layout.
 *
 * ```typescript
 *
 *   import {CreateGeofenceCardContext} from
 *     '@ion/dls/src/maps/geofences/CreateGeofenceCard';
 *   ...
 *
 *   const createCard = useContext(CreateGeofenceCardContext);
 *
 * ```
 */
export const CreateGeofenceCardContext = createContext<CreateGeofenceCardContextValue | null>(null);

const GEOFENCE_TYPE_OPTIONS: GeofenceTypeOption[] = [
  {type: CreateGeoFenceTypeEnum.Radial, key: 'radial', Icon: GeofencesRadialIcon},
  {type: CreateGeoFenceTypeEnum.Polygon, key: 'polygon', Icon: GeofencesPolygonIcon},
  {type: CreateGeoFenceTypeEnum.Draw, key: 'draw', Icon: GeofencesDrawIcon}
];

const getOptionKey = (geofenceType: CreateGeoFenceTypeEnum): GeofenceTypeOption['key'] => (
  GEOFENCE_TYPE_OPTIONS.find(function(option) {
    return option.type === geofenceType;
  })?.key ?? 'radial'
);

const useStyles = makeStyles(() => ({
  typeSection : {
    padding : '8px 16px'
  },
  typeSwitcher : {
    display         : 'flex',
    gap             : 4,
    padding         : 4,
    borderRadius    : 8,
    backgroundColor : dlsColor('grey6')
  },
  typeOption : {
    flex            : 1,
    height          : 59,
    boxSizing       : 'border-box',
    display         : 'flex',
    flexDirection   : 'column',
    alignItems      : 'center',
    justifyContent  : 'center',
    gap             : 4,
    padding         : '4px 8px',
    border          : `1px solid ${dlsColor('grey5')}`,
    borderRadius    : 8,
    backgroundColor : dlsColor('white'),
    cursor          : 'pointer',
    fontFamily      : 'Inter',
    fontWeight      : 500,
    fontSize        : '12px',
    lineHeight      : '16px',
    color           : dlsColor('grey1')
  },
  typeOptionSelected : {
    border          : '1px solid transparent',
    backgroundColor : dlsColor('paleBlue'),
    color           : dlsColor('darkTeal')
  }
}));

/**
 * Renders the current theme's create geofence card: a Radial / Polygon /
 * Draw switcher, the selected type's description, the address search, the
 * radius control (radial only), and Cancel / Create actions. Create is
 * disabled until a shape has been placed on the map.
 *
 * ```typescript
 *
 *   import {CreateGeofenceCard} from
 *     '@ion/dls/src/maps/geofences/CreateGeofenceCard';
 *   ...
 *
 *   <CreateGeofenceCard selectedGeofenceType={CreateGeoFenceTypeEnum.Radial}
 *     onChangeGeofenceType={onChangeGeofenceType}
 *     onClose={onClose}
 *     onCreate={onCreate}
 *     isCreateDisabled={!drawingComplete}
 *     search={<SearchAddress mapView={view} hideLabel />}
 *     radius={radius} />
 *
 * ```
 *
 * @param   selectedGeofenceType The selected geofence type.
 * @param   onChangeGeofenceType Invoked with the newly selected type.
 * @param   onClose              Invoked by the close and Cancel buttons.
 * @param   onCreate             Invoked by the Create button.
 * @param   isCreateDisabled     Disables the Create button when `true`.
 * @param   search               The address search field.
 * @param   radius               The radius control's values, if shown.
 * @returns                      The create geofence card.
 */
export const CreateGeofenceCard = ({
  selectedGeofenceType,
  onChangeGeofenceType,
  onClose,
  onCreate,
  isCreateDisabled,
  search,
  radius = null
}: CreateGeofenceCardProps) => {
  const cardClasses = useGeofenceCardStyles();
  const classes     = useStyles();
  const optionKey   = getOptionKey(selectedGeofenceType);

  return (
    <>
      <CreateGeofenceCardHeader onClose={onClose} />
      <div className={classes.typeSection}>
        <div className={classes.typeSwitcher} role='radiogroup'>
          {GEOFENCE_TYPE_OPTIONS.map(function({type, key, Icon}) {
            const isSelected = type === selectedGeofenceType;
            return (
              <button key={type}
                type='button'
                role='radio'
                aria-checked={isSelected}
                className={classnames(classes.typeOption, isSelected && classes.typeOptionSelected)}
                onClick={function() {
                  onChangeGeofenceType(type);
                }}>
                <Icon size={IconSize.medium} color={dlsColor(isSelected ? 'darkTeal' : 'grey1')} />
                {translate(`maps:geofences.createCard.${key}`)}
              </button>
            );
          })}
        </div>
      </div>
      <div className={cardClasses.body}>
        <p className={cardClasses.description}>
          {translate(`maps:geofences.createCard.${optionKey}Description`)}
        </p>
        <div className={cardClasses.searchGroup}>
          <span className={cardClasses.label}>
            {translate(`maps:geofences.createCard.${optionKey}SearchLabel`)}
          </span>
          <div className={cardClasses.searchField}>
            {search}
          </div>
        </div>
        {radius && <GeofenceRadiusControl radius={radius} />}
        <div className={cardClasses.actions}>
          <TertiaryButton onClick={onClose}>
            {translate('maps:geofences.cancel')}
          </TertiaryButton>
          <PrimaryButton disabled={isCreateDisabled} onClick={onCreate}>
            {translate('maps:geofences.createCard.create')}
          </PrimaryButton>
        </div>
      </div>
    </>
  );
};

export default CreateGeofenceCard;
